'use client';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Container,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useMemo } from 'react';
import slugify from 'react-slugify';

import LinkCustom from '@/app/_components/link/LinkCustom';
import { ReactComponent as HomeIcon } from '@/app/_icons/home.svg';
import { ReactComponent as VectorLocation } from '@/app/_icons/location-vector.svg';
import { useRoutesConfig } from '@/hooks/routes-config';
import { VehicleExtended } from '@/types/details-car/details-car.types';

import { PriceFormat } from '../price-format/price-format';

import Styles from './HeaderSection.module.scss';

const HeaderSection = ({ vehicleInfo }: { vehicleInfo: VehicleExtended }) => {
  const router = useRouter();
  const routes = useRoutesConfig();
  const pricing = useMemo(() => {
    const now = Number(vehicleInfo?.nowPrice || 0);
    const normal = Number(vehicleInfo?.price || 0);
    const reprice = Number(vehicleInfo?.reprice_price || 0);
    const basePrice = now > 0 ? now : normal;

    if (vehicleInfo?.do_not_reprice || !reprice || reprice <= 0) {
      return {
        displayPrice: basePrice,
        originalPrice: null,
      };
    }

    // If reprice lower → show discount
    if (reprice < basePrice) {
      return {
        displayPrice: reprice,
        originalPrice: basePrice,
      };
    }

    // If reprice higher → show only reprice
    if (reprice > basePrice) {
      return {
        displayPrice: reprice,
        originalPrice: null,
      };
    }

    // Equal case
    return {
      displayPrice: basePrice,
      originalPrice: null,
    };
  }, [vehicleInfo]);

  const monthlyRepayment = useMemo(() => {
    const repriceMonthly = Number(
      vehicleInfo?.reprice_monthly_repayment || 0
    );

    if (!vehicleInfo?.do_not_reprice && repriceMonthly > 0) {
      return repriceMonthly;
    }

    return vehicleInfo?.monthlyRepayment || null;
  }, [vehicleInfo]);

  const urls = [
    {
      url: `${routes.usedCars.path}`,
      name: 'Certified Pre-Owned',
    },
    {
      url: `${routes.usedCars.path}/${slugify(vehicleInfo?.brand)}`,
      name: vehicleInfo.brand,
    },
    {
      url: `${routes.usedCars.path}/${slugify(
        vehicleInfo?.brand
      )}/${slugify(vehicleInfo?.range_id)}`,
      name: vehicleInfo.range_id,
    },
    {
      url: `${routes.usedCars.path}/${slugify(
        vehicleInfo?.brand
      )}/${slugify(vehicleInfo?.range_id)}/${slugify(
        vehicleInfo?.id
      )}`,
      name: vehicleInfo?.title,
    },
  ];

  return (
    <Container
      maxW={'1328'}
      width={'100%'}
      className={Styles.headerSectionWrapper}
      paddingTop={{ base: 12, md: 14 }}
      paddingX={{ base: 4, xl: 0 }}
    >
      <Breadcrumb marginBottom={12} className={Styles.breadcrumb}>
        <BreadcrumbItem>
          <BreadcrumbLink
            as={Link}
            href={routes.root.path}
            className={Styles.breadcrumbLink}
          >
            <HomeIcon style={{ marginRight: '8px' }} />
          </BreadcrumbLink>
        </BreadcrumbItem>

        {urls.map((path, i) => (
          <BreadcrumbItem
            key={i}
            isCurrentPage={i === urls.length - 1}
          >
            <BreadcrumbLink
              href={path.url}
              className={Styles.breadcrumbLink}
            >
              <Text as={'span'}>{path.name}</Text>
            </BreadcrumbLink>
          </BreadcrumbItem>
        ))}
      </Breadcrumb>

      <Box className={Styles.bakcBtnContainer} marginBottom={10}>
        <Button
          onClick={() => router.back()}
          className={Styles.backBtn}
          padding={0}
        >
          <ChevronLeftIcon />
          <Box className={Styles.backBtnText}>
            Back to Results
          </Box>
        </Button>
      </Box>

      <Flex
        justifyContent={{ base: 'center', lg: 'space-between' }}
        direction={{ base: 'column', lg: 'row' }}
        alignItems={{ base: 'center', lg: 'flex-start' }}
        gap={6}
        textAlign={{ base: 'center', lg: 'left' }}
      >
        <Box className={Styles.headingElement} flex={1} minWidth={0}>
          <Heading as="h1" className={Styles.titleLg}>
            {vehicleInfo?.title}
          </Heading>
        </Box>

        <Box
          className={Styles.headingElement}
          display={'flex'}
          flexDirection={'column'}
          alignItems={{ base: 'center', lg: 'flex-end' }}
          flexShrink={0}
          minWidth={'fit-content'}
        >
          {/* Main Price */}
          <Heading as="h2" className={Styles.titleMd} marginBottom={2}>
            <PriceFormat
              value={pricing.displayPrice}
              thousandSeparator=" "
              prefix="R"
            />
          </Heading>

          {/* Strike-through original price */}
          {pricing.originalPrice && (
            <Text
              className={Styles.titleText}
              textDecoration="line-through"
              color="red.500"
            >
              Was{' '}
              <PriceFormat
                value={pricing.originalPrice}
                thousandSeparator=" "
                prefix="R"
              />
            </Text>
          )}

          {/* Monthly repayment */}
          {monthlyRepayment && (
            <Text className={Styles.titleText}>
              From{' '}
              <PriceFormat
                value={monthlyRepayment}
                thousandSeparator=","
                prefix="R"
                suffix=" p/m"
              />
            </Text>
          )}
        </Box>
      </Flex>

      <Flex
        justifyContent={{ base: 'center', lg: 'space-between' }}
        direction={{ base: 'column', lg: 'row' }}
        alignItems={{ base: 'center', lg: 'flex-start' }}
        gap={6}
        textAlign={{ base: 'center', lg: 'left' }}
        marginTop={{ base: '22px', sm: '0px' }}
      >
        <Link
          href="#contact-section"
          passHref
          className={Styles.dealershipLocation}
        >
          <VectorLocation /> {vehicleInfo?.dealership_id?.name}
        </Link>

        {vehicleInfo?.digitalTwinUrl &&
          vehicleInfo?.digitalTwinUrl?.length > 0 && (
            <Box mt={10}>
              <LinkCustom
                href={vehicleInfo?.digitalTwinUrl}
                extraClass={Styles.ctaBtn}
                target="_blank"
                rel="noopener noreferrer"
              >
                Vehicle History
              </LinkCustom>
            </Box>
          )}
      </Flex>
    </Container>
  );
};

export default HeaderSection;